import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DataTable,
  EntityCardGrid,
  StatusBadge,
  TableShell,
  type ViewMode
} from '@repletoryreach/sdk-ui/data-display';
import { Group, Stack, Text } from '@repletoryreach/sdk-ui';
import type { admin } from '@repletoryreach/sdk-api';
import { useUsers } from '../../../../data/useUsers';
import { usePersistedState } from '../../../../lib/usePersistedState';
import type { Indexed } from '../../../../lib/types';
import { formatDate, formatRelative } from '../../../../lib/format';
import { TwoLineCell } from '../../../../components/TwoLineCell';
import { PersonAvatar } from '../../../users/components/PersonAvatar';
import { useT } from '../../../../i18n/I18nProvider';

type AdminUser = NonNullable<Awaited<ReturnType<typeof admin.listUsers>>['data']>[number];

const MUTED = 'var(--rr-color-text-muted)';

/** `email` is typed non-null in the SDK but nullable in the database, so every read guards. */
function emailOf(u: AdminUser): string {
  return u.email ?? '';
}

function nameOf(u: AdminUser, fallback: string): string {
  return u.display_name || u.email || fallback;
}

export function UsersTab({ tenantId }: { tenantId: string }) {
  const t = useT();
  const nav = useNavigate();
  // Cross-tenant /v1/admin/users currently has no org filter: we filter client-side
  // until the endpoint accepts ?org_id=. Acceptable scale for the admin console.
  const { data, isLoading } = useUsers();
  const scoped = useMemo(
    () => (data?.data ?? []).filter((u) => u.organisation_id === tenantId),
    [data, tenantId]
  );

  const [view, setView] = usePersistedState<ViewMode>('admin.tenant.users.view', 'table');
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return scoped;
    return scoped.filter(
      (u) =>
        emailOf(u).toLowerCase().includes(q) ||
        (u.display_name ?? '').toLowerCase().includes(q)
    );
  }, [scoped, query]);

  const unnamed = t('users.unnamed');

  function scopeBadge(scope: string) {
    return (
      <StatusBadge size="xs" status={scope === 'platform' ? 'warning' : 'pending'}>
        {scope === 'platform' ? t('users.scope.platform') : t('users.scope.tenant')}
      </StatusBadge>
    );
  }

  return (
    <TableShell
      title={t('tenants.view.users.title')}
      totalLabel={t('tenants.view.users.totalLabel', { count: filtered.length, total: scoped.length })}
      search={query}
      onSearch={setQuery}
      searchPlaceholder={t('tenants.view.users.searchPlaceholder')}
      view={view}
      onViewChange={setView}
      loading={isLoading}
      isEmpty={filtered.length === 0}
      empty={
        <Text role="bodySmall" c={MUTED}>
          {query ? t('tenants.view.users.noMatch') : t('tenants.view.users.empty')}
        </Text>
      }
    >
      {view === 'table' && (
        <DataTable<Indexed<AdminUser>>
          records={filtered as Indexed<AdminUser>[]}
          loading={isLoading}
          idAccessor="id"
          onRowClick={(r) => nav(`/users/${r.id}`)}
          emptyState={t('tenants.view.users.empty')}
          columns={[
            {
              accessor: 'display_name',
              title: t('users.columns.user'),
              sortable: true,
              render: (r) => (
                <TwoLineCell
                  primary={nameOf(r, unnamed)}
                  secondary={emailOf(r) || t('users.noEmail')}
                  title={emailOf(r) || undefined}
                  leading={<PersonAvatar name={nameOf(r, unnamed)} />}
                />
              )
            },
            {
              accessor: 'scope',
              title: t('tenants.view.users.columns.scope'),
              render: (r) => scopeBadge(r.scope)
            },
            {
              accessor: 'org_role',
              title: t('tenants.view.users.columns.role'),
              render: (r) => r.org_role ? <StatusBadge size="xs" status={r.org_role === 'owner' ? 'healthy' : 'pending'}>{t(`tenants.view.users.role.${r.org_role}`, r.org_role)}</StatusBadge> : null
            },
            {
              accessor: 'created_at',
              title: t('users.columns.joined'),
              sortable: true,
              render: (r) => <TwoLineCell primary={formatDate(r.created_at)} secondary={formatRelative(r.created_at)} />
            }
          ]}
        />
      )}
      {view === 'list' && (
        <Stack gap="sm">
          {filtered.map((u) => (
            <button
              key={u.id}
              type="button"
              onClick={() => nav(`/users/${u.id}`)}
              className="rr-list-row"
            >
              <Group gap="md" align="center" wrap="nowrap" style={{ width: '100%' }}>
                <span style={{ flexShrink: 0, display: 'inline-flex' }}>
                  <PersonAvatar name={nameOf(u, unnamed)} size="md" />
                </span>
                <Stack gap="xs" style={{ flex: 1, minWidth: 0 }}>
                  <Text role="subtitle" fw={600} truncate>
                    {nameOf(u, unnamed)}
                  </Text>
                  <Text role="caption" c={MUTED}>
                    {emailOf(u) || unnamed} · {t('tenants.view.users.createdRelative', { when: formatRelative(u.created_at) })}
                  </Text>
                </Stack>
                {scopeBadge(u.scope)}
              </Group>
            </button>
          ))}
        </Stack>
      )}
      {view === 'cards' && (
        <EntityCardGrid<AdminUser>
          records={filtered}
          idAccessor="id"
          cols={{ base: 1, sm: 2, lg: 3 }}
          spacing="md"
          onCardClick={(u) => nav(`/users/${u.id}`)}
          renderCard={(u) => (
            <Stack gap="md">
              <Group gap="sm" align="center" wrap="nowrap">
                <PersonAvatar name={nameOf(u, unnamed)} size="md" />
                <Stack gap="xs" style={{ minWidth: 0 }}>
                  <Text role="subtitle" fw={600} truncate>
                    {nameOf(u, unnamed)}
                  </Text>
                  <Text role="caption" c={MUTED} truncate>
                    {emailOf(u) || unnamed}
                  </Text>
                </Stack>
              </Group>
              {/* The card used to close with eight characters of the user's GUID.
                  A person is identified by name and email; the id belongs in the URL. */}
              <Group justify="space-between" align="baseline" style={{ paddingTop: 8, borderTop: '1px solid var(--rr-color-border)' }}>
                {scopeBadge(u.scope)}
                <Text role="caption" c={MUTED}>
                  {t('tenants.view.users.createdAt', { date: formatDate(u.created_at) })}
                </Text>
              </Group>
            </Stack>
          )}
        />
      )}
    </TableShell>
  );
}
